import {
  AfterViewInit,
  ChangeDetectionStrategy, ChangeDetectorRef,
  Component, ElementRef,
  EmbeddedViewRef,
  EventEmitter, forwardRef, Inject,
  Input, NgZone,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef, ViewChild, ViewContainerRef
} from '@angular/core';
import Popper from 'popper.js';
import {debounceTime, filter, takeUntil} from 'rxjs/operators';
import {fromEvent, Subject} from 'rxjs';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator
} from '@angular/forms';
import { untilDestroyed } from 'ngx-take-until-destroy';
import {ErrorStateMatcherUtil} from '../../utils/errorStateMatcher.util';
import {MatOption, MatSelect} from '@angular/material';


@Component({
  selector: 'app-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.css'],
  // changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => SelectComponent),
      multi: true,
    }
  ]

})
export class SelectComponent implements OnInit, OnDestroy, ControlValueAccessor, Validator  {
  @Input() model;
  @Input() label;
  @Input() info = '';
  @Input() labelKey = 'label';
  @Input() idKey = 'id';
  @Input() objKey = false;
  @Input() isArrayOfString = false;
  @Input() options = [];
  @Input() searchable = false;
  @Input() multiple = false;
  @Input() maxItemsSelect: number;
  @Input() optionTpl: TemplateRef<any>;
  @Input() disableItem: any = null;
  @Output() selectChange = new EventEmitter();
  @Output() closed = new EventEmitter();

  @ViewChild('mselect', null) matSelect: MatSelect;
  @ViewChild('searchSelectInput', null) searchSelectInput: ElementRef;


  visibleOptions = 4;
  control: AbstractControl;
  searchControl = new FormControl();
  value: any;
  disable = false;
  selectListControl = new FormControl();
  matcher = new ErrorStateMatcherUtil();

  private view: EmbeddedViewRef<any>;
  private popperRef: Popper;
  private originalOptions = [];

  /** Previously selected values when using <mat-select [multiple]="true"> */
  private previousSelectedValues: any[];

  /** Subject that emits when the component has been destroyed. */
  // tslint:disable-next-line:variable-name
  private _onDestroy = new Subject<void>();

  constructor(
    private vcr: ViewContainerRef,
    private zone: NgZone,
    private cdr: ChangeDetectorRef,
  ) {}

  get isOpen() {
    return !!this.popperRef;
  }

  ngOnInit() {
    this.originalOptions = [...this.options];
    if (this.model !== undefined) {
      this.model = this.options.find(currentOption => currentOption[this.idKey] === this.model);
    }

    this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        untilDestroyed(this)
      )
      .subscribe(term => this.search(term));

    if (this.searchable) {
      this.matSelect.openedChange
        .pipe(takeUntil(this._onDestroy))
        .subscribe((opened) => {
          if (opened) {
            // focus the search field when opening
            this.searchSelectInput.nativeElement.focus();
          } else {
            // clear it when closing
            this.searchSelectInput.nativeElement.value = '';
            this.searchControl.reset('');
          }
        });
    }


  }

  registerOnChange(fn: (value: any) => void): void {
    // this.control.valueChanges
    //   .subscribe(fn);
    // console.log(typeof fn);
    this.propagateChange = fn;
    if (this.multiple) {
      this.initMultipleHandling();

    }
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  private propagateChange = (_: any) => {};

  private onTouched: () => void = () => {
    this.control.markAsDirty({onlySelf: true});
  };

  writeValue(value: any): void {
    if (value) {
      this.value = value;
      this.previousSelectedValues = this.value;
      // this.control.setValue(value);
    } else {
      this.value = this.multiple ? [] : null;
      // this.control.reset(this.multiple ? [] : null);
    }
  }

  validate(control: AbstractControl): ValidationErrors | null {
    this.control = control;
    return undefined;
  }

  registerOnValidatorChange(fn: () => void): void {}

  get placeholder() {
    return this.model ? this.model[this.labelKey] : 'בחר...';
  }

  onChange(event) {
    this.value = event.value;
    if (this.multiple) {
        this.disable = event.value.length >= this.maxItemsSelect;
    }
    this.propagateChange(event.value);
    this.selectChange.emit(event.value);
  }

  search(value: string) {
    this.options = this.originalOptions.filter(option => option[this.labelKey].includes(value));
    requestAnimationFrame(() => (this.visibleOptions = this.options.length || 1));
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  private initMultipleHandling() {
    this.matSelect.valueChange
      .pipe(takeUntil(this._onDestroy))
      .subscribe((values) => {
        if (this.matSelect.multiple) {
          let restoreSelectedValues = false;
          if (this.value && this.value.length
            && this.previousSelectedValues && Array.isArray(this.previousSelectedValues)) {
            if (!values || !Array.isArray(values)) {
              values = [];
            }
            const optionValues = this.matSelect.options.map(option => option.value);
            this.previousSelectedValues.forEach(previousValue => {
              if (values.indexOf(previousValue) === -1 && optionValues.indexOf(previousValue) === -1) {
                // if a value that was selected before is deselected and not found in the options, it was deselected
                // due to the filtering, so we restore it.
                values.push(previousValue);
                restoreSelectedValues = true;
              }
            });
          }

          if (restoreSelectedValues) {
            this.matSelect._onChange(values);

          }

          this.previousSelectedValues = values;
        }
      });
  }

  clearSelected() {
      const newValue = this.multiple ? [] : null;

      this.control.setValue(newValue);
      this.value = newValue;
      this.previousSelectedValues = newValue;

      this.disable = false; 

      this.propagateChange(newValue);

     if (this.matSelect) {
      this.matSelect.writeValue(newValue);
     }

  this.cdr.detectChanges();
  }




}
